// src/hooks/assistant/useAssistantController.ts
/**
 * Assistant Controller Hook
 * 
 * Page-level state coordination for the assistant page.
 * Manages conversation selection, deletion, and chat props.
 */

import { useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import { deleteConversation as deleteConversationApi, type ConversationMeta } from "@/services/assistant/AssistantService";
import { useConversations } from "@/hooks/assistant/useAssistant";
import { useAccessToken } from "@/hooks/useAccessToken";
import { useQueryClient } from "@tanstack/react-query";
import { assistantQueryKeys } from "@/constants/queryKeys";
import { useAssistantStore, selectStreamingById } from "@/stores/assistantStore";

export const useAssistantController = () => {
  // Zustand store selectors
  const activeConversationId = useAssistantStore((s) => s.activeConversationId);
  const setActiveConversationId = useAssistantStore((s) => s.setActiveConversationId);
  const clearConversation = useAssistantStore((s) => s.clearConversation);
  
  // Use useShallow for the streaming selector to prevent unnecessary re-renders
  // useShallow does shallow comparison of the returned object
  const streamingById = useAssistantStore(useShallow(selectStreamingById));

  // Track reset nonce for triggering new chat
  const resetNonceRef = useRef(0);
  const endRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  // Get auth token from centralized Redux store
  const authToken = useAccessToken();
  const { data: conversationsData, isLoading: conversationsLoading } = useConversations(authToken, 50);

  // Suggestions are now mode-specific and defined in ChatEmptyState
  // No longer needed here

  function startNewChat() {
    // Do NOT stop or reset any existing streams; allow parallel streaming.
    setActiveConversationId(undefined);
    resetNonceRef.current += 1;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  async function deleteConversation(conversationId: string) {
    try {
      await deleteConversationApi(conversationId, authToken);
      if (activeConversationId === conversationId) {
        setActiveConversationId(undefined);
      }
      clearConversation(conversationId);
      queryClient.invalidateQueries({ queryKey: assistantQueryKeys.conversations.all() });
    } catch {
      // Deletion failed - user can retry
    }
  }

  const sortedConversations = useMemo(() => {
    const items = (conversationsData?.data || []) as ConversationMeta[];
    return [...items].sort((a, b) => (b.updated_at_ms || 0) - (a.updated_at_ms || 0));
  }, [conversationsData?.data]);

  // Callbacks for AssistantChat
  const chatProps = {
    conversationId: activeConversationId,
    authToken,
    // suggestions are now mode-specific and defined in ChatEmptyState
    onConversationId: (cid: string, title?: string) => {
      setActiveConversationId(cid);
      const now = Date.now();
      const optimistic: ConversationMeta = {
        conversation_id: cid,
        title: title || cid,
        agent: "all",
        created_at_ms: now,
        updated_at_ms: now,
      } as ConversationMeta;
      queryClient.setQueriesData<{ data: ConversationMeta[] }>(
        { queryKey: assistantQueryKeys.conversations.all() },
        (prev) => {
          const existing = prev?.data ?? [];
          if (existing.some((c) => c.conversation_id === cid)) return { data: existing };
          return { data: [optimistic, ...existing] };
        }
      );
    },
    onStreamingChange: (_cid: string, streaming: boolean) => {
      // Streaming state is now managed by Zustand store automatically
      // Just invalidate queries when streaming ends
      if (!streaming) {
        queryClient.invalidateQueries({ queryKey: assistantQueryKeys.conversations.all() });
      }
    },
    onConversationsShouldRefresh: () => {
      queryClient.invalidateQueries({ queryKey: assistantQueryKeys.conversations.all() });
    },
    resetNonce: resetNonceRef.current,
  } as const;

  return {
    // Data
    activeConversationId,
    streamingById,
    conversations: sortedConversations,
    conversationsLoading,
    sortedConversations,
    // Actions
    setActive: (cid: string | undefined) => setActiveConversationId(cid),
    startNewChat,
    deleteConversation,
    // Chat props to spread into AssistantChat
    chatProps,
    // misc
    endRef,
  } as const;
};

export type UseAssistantControllerReturn = ReturnType<typeof useAssistantController>;
